import { useEffect, useMemo, useState, useRef } from "react";
import { Link } from "react-router-dom";
import {
  Brain, Sparkles, Loader2, CheckCircle2, AlertTriangle, RefreshCw,
  X as XIcon, Send, Clock, MessageSquare, Mic, MicOff,
} from "lucide-react";
import api from "../../lib/api";
import Modal from "../Modal";
import useVoiceCapture from "../../lib/useVoiceCapture";
import { useBranding } from "../../context/TenantBrandingContext";
import DictTextarea from "../DictTextarea";

/**
 * Wave 19 #103 — Deep Itinerary Portfolio + Post-Trip Debrief.
 *
 * Three exports:
 *   <DeepModePill> — header toggle (next to Active/Locked)
 *   <DeepItineraryPanel> — banner + compile button + rendered AI doc
 *   <DebriefModal> — post-trip Hidro chat that captures meeting notes
 *
 * Honours the Long-Running AI Contract: long compiles run in the
 * background; the user gets a notification when ready, and the panel
 * polls the GET endpoint until status flips from `compiling` → `ready`.
 */

// ─── Tiny markdown renderer (good enough for the AI deep doc) ─────────
function renderInline(text) {
  if (!text) return null;
  const parts = [];
  const re = /(\[([^\]]+)\]\(([^)]+)\))|(\*\*([^*]+)\*\*)|(\*([^*]+)\*)|(`([^`]+)`)/g;
  let last = 0;
  let m;
  let key = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    if (m[1]) {
      const label = m[2];
      const href = m[3];
      const internal = href.startsWith("/");
      parts.push(
        internal ? (
          <Link key={`l${key++}`} to={href} className="text-[#1fb6c1] underline underline-offset-2">{label}</Link>
        ) : (
          <a key={`l${key++}`} href={href} target="_blank" rel="noreferrer noopener" className="text-[#1fb6c1] underline underline-offset-2">{label}</a>
        ),
      );
    } else if (m[4]) {
      parts.push(<strong key={`b${key++}`} className="font-bold text-white">{m[5]}</strong>);
    } else if (m[6]) {
      parts.push(<em key={`i${key++}`} className="italic">{m[7]}</em>);
    } else if (m[8]) {
      parts.push(<code key={`c${key++}`} className="px-1 py-0.5 rounded bg-white/10 text-[#7adde5] text-[0.85em] font-mono">{m[9]}</code>);
    }
    last = re.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

function MarkdownView({ md }) {
  const blocks = useMemo(() => {
    if (!md) return [];
    const lines = md.split("\n");
    const out = [];
    let para = [];
    let listItems = [];
    let listType = null; // 'ul' | 'ol'
    let inCode = false;
    let codeBuf = [];
    let bq = [];
    const flushPara = () => { if (para.length) { out.push({ t: "p", text: para.join(" ") }); para = []; } };
    const flushList = () => { if (listItems.length) { out.push({ t: listType === "ol" ? "ol" : "ul", items: listItems }); listItems = []; listType = null; } };
    const flushBq = () => { if (bq.length) { out.push({ t: "bq", text: bq.join(" ") }); bq = []; } };
    for (const raw of lines) {
      const line = raw.replace(/\s+$/, "");
      if (line.startsWith("```")) {
        flushPara(); flushList(); flushBq();
        if (inCode) { out.push({ t: "code", text: codeBuf.join("\n") }); codeBuf = []; inCode = false; }
        else { inCode = true; }
        continue;
      }
      if (inCode) { codeBuf.push(line); continue; }
      const h = line.match(/^(#{1,6})\s+(.*)$/);
      if (h) { flushPara(); flushList(); flushBq(); out.push({ t: `h${h[1].length}`, text: h[2] }); continue; }
      if (/^\s*>\s+/.test(line)) { flushPara(); flushList(); bq.push(line.replace(/^\s*>\s+/, "")); continue; }
      const ul = line.match(/^\s*[-*+]\s+(.*)$/);
      if (ul) { flushPara(); flushBq(); if (listType !== "ul") flushList(); listType = "ul"; listItems.push(ul[1]); continue; }
      const ol = line.match(/^\s*\d+\.\s+(.*)$/);
      if (ol) { flushPara(); flushBq(); if (listType !== "ol") flushList(); listType = "ol"; listItems.push(ol[1]); continue; }
      if (line.trim() === "") { flushPara(); flushList(); flushBq(); continue; }
      flushList(); flushBq();
      para.push(line);
    }
    flushPara(); flushList(); flushBq();
    if (inCode && codeBuf.length) out.push({ t: "code", text: codeBuf.join("\n") });
    return out;
  }, [md]);

  return (
    <div className="hv-deep-doc text-[#dde3ec] leading-relaxed text-sm space-y-3">
      {blocks.map((b, i) => {
        if (b.t === "h1") return <h1 key={i} className="font-display text-2xl md:text-3xl font-bold text-[#1fb6c1] mt-6 first:mt-0 pb-1 border-b border-[#1fb6c1]/30">{renderInline(b.text)}</h1>;
        if (b.t === "h2") return <h2 key={i} className="font-display text-lg md:text-xl font-bold text-white mt-4">{renderInline(b.text)}</h2>;
        if (b.t === "h3") return <h3 key={i} className="font-display text-base font-semibold text-[#a0aabf] mt-3">{renderInline(b.text)}</h3>;
        if (b.t === "h4" || b.t === "h5" || b.t === "h6") return <h4 key={i} className="font-semibold text-sm text-[#a0aabf] mt-2">{renderInline(b.text)}</h4>;
        if (b.t === "p") return <p key={i} className="text-sm leading-relaxed">{renderInline(b.text)}</p>;
        if (b.t === "ul") return (
          <ul key={i} className="space-y-1.5 list-disc pl-5 marker:text-[#1fb6c1]/60">
            {b.items.map((it, j) => <li key={j} className="text-sm">{renderInline(it)}</li>)}
          </ul>
        );
        if (b.t === "ol") return (
          <ol key={i} className="space-y-1.5 list-decimal pl-5 marker:text-[#1fb6c1]/60">
            {b.items.map((it, j) => <li key={j} className="text-sm">{renderInline(it)}</li>)}
          </ol>
        );
        if (b.t === "bq") return (
          <blockquote key={i} className="border-l-4 border-[#1fb6c1] pl-3 py-2 bg-[#1fb6c1]/5 italic text-[#dde3ec] text-sm rounded-r">
            {renderInline(b.text)}
          </blockquote>
        );
        if (b.t === "code") return (
          <pre key={i} className="bg-black/30 border border-white/10 rounded p-2 text-xs font-mono text-[#a0aabf] overflow-auto whitespace-pre-wrap">{b.text}</pre>
        );
        return null;
      })}
    </div>
  );
}

// ─── Header pill: toggle Deep Mode ────────────────────────────────────
export function DeepModePill({ trip, onToggled }) {
  const [busy, setBusy] = useState(false);
  if (!trip?.id) return null;
  const enabled = !!trip.deep_mode;
  const onClick = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/deep-mode`, { enabled: !enabled });
      onToggled?.(!!data.deep_mode);
    } catch (_e) {
      // noop
    }
    setBusy(false);
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      data-testid="trip-deep-mode-pill"
      title={enabled ? "Deep Mode is on — pick stops and compile a portfolio itinerary in the Itinerary tab" : "Activate Deep Mode to unlock per-stop deep research and the AI portfolio compile"}
      className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border transition-colors ${
        enabled
          ? "border-[#A78BFA]/60 bg-[#A78BFA]/10 text-[#C4B5FD]"
          : "border-white/15 text-[#a0aabf] hover:border-[#A78BFA]/50 hover:text-[#C4B5FD]"
      }`}
    >
      <Brain className="w-3 h-3" />
      {busy ? "…" : (enabled ? "Deep Mode · ON" : "Activate Deep Mode")}
    </button>
  );
}

// ─── Stop-picker modal: which stops get a deep brief ──────────────────
function CompileDeepModal({ trip, onClose, onLaunched }) {
  const stops = trip?.stops || [];
  const initialPicked = useMemo(
    () => new Set(stops.filter((s) => s.lead_id || s.customer_id).map((s) => s.id)),
    [stops],
  );
  const [picked, setPicked] = useState(initialPicked);
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const toggle = (sid) => {
    setPicked((prev) => {
      const n = new Set(prev);
      n.has(sid) ? n.delete(sid) : n.add(sid);
      return n;
    });
  };
  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      const body = { stop_ids: [...picked], force };
      const { data } = await api.post(`/trips/${trip.id}/compile-deep-itinerary`, body);
      onLaunched?.(data);
      onClose?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not start compile");
    }
    setBusy(false);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Compile Deep Itinerary"
      testId="trip-deep-compile-modal"
      maxWidth="max-w-xl"
      footer={
        <div className="flex justify-between items-center gap-3">
          <label className="flex items-center gap-2 text-[11px] text-[#a0aabf] cursor-pointer">
            <input
              type="checkbox"
              checked={force}
              onChange={(e) => setForce(e.target.checked)}
              data-testid="deep-compile-force"
            />
            Force-refresh briefs (ignore 7-day cache)
          </label>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="hv-btn hv-btn-ghost text-xs">
              <XIcon className="w-3 h-3" /> Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy || picked.size === 0}
              className="hv-btn hv-btn-primary text-xs"
              data-testid="deep-compile-launch"
            >
              <Sparkles className="w-3 h-3" />
              {busy ? "Launching…" : `Compile ${picked.size} stop${picked.size === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="text-xs text-[#a0aabf]">
          Pick the stops you want a <strong className="text-white">full deep research brief</strong> for — tourism rating,
          published rates, best & worst Google review, contacts, and an AI pitch hook.
          Hidro then composes a long-form portfolio itinerary that ties everything together.
          You can leave the modal — we'll notify you when it's ready (typically 3–8 minutes).
        </p>
        <div className="space-y-1 max-h-[55vh] overflow-auto pr-1">
          {stops.map((s, i) => {
            const has = !!(s.lead_id || s.customer_id);
            const checked = picked.has(s.id);
            return (
              <label
                key={s.id}
                className={`flex items-start gap-2 px-2 py-2 rounded border cursor-pointer transition-colors ${
                  checked ? "border-[#1fb6c1]/50 bg-[#1fb6c1]/5" : "border-white/10 hover:border-white/20"
                } ${!has ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={!has}
                  onChange={() => has && toggle(s.id)}
                  data-testid={`deep-compile-stop-${s.id}`}
                  className="mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">
                    Stop {i + 1}: {s.title || s.address || "Unnamed stop"}
                  </div>
                  <div className="text-[10px] text-[#647087] truncate">
                    {s.address || "—"} {s.duration_min ? `· ${s.duration_min} min on-site` : ""}
                  </div>
                  {!has && (
                    <div className="text-[10px] text-amber-400 mt-0.5">
                      Not linked to a lead or customer — link it first to enable deep research.
                    </div>
                  )}
                </div>
              </label>
            );
          })}
          {stops.length === 0 && (
            <div className="text-center text-xs text-[#647087] py-6">No stops on this trip yet.</div>
          )}
        </div>
        {err && (
          <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1.5">
            {err}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── Main panel: lives on the Itinerary tab above the legacy doc ──────
export function DeepItineraryPanel({ trip, onTripChange }) {
  const [data, setData] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const pollRef = useRef(null);

  const refresh = async () => {
    if (!trip?.id) return;
    try {
      const { data: d } = await api.get(`/trips/${trip.id}/deep-itinerary`);
      setData(d);
      // Reflect deep_mode change back to parent so the header pill stays in sync
      if (d && typeof d.deep_mode === "boolean" && d.deep_mode !== !!trip.deep_mode) {
        onTripChange?.((t) => ({ ...t, deep_mode: d.deep_mode }));
      }
    } catch (_e) { /* noop */ }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [trip?.id]);

  // Poll every 6s while compiling so the user sees progress + the doc
  // appears as soon as the background job finishes.
  useEffect(() => {
    if (data?.status !== "compiling") {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    if (pollRef.current) return;
    pollRef.current = setInterval(refresh, 6000);
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    // eslint-disable-next-line
  }, [data?.status]);

  if (!trip?.deep_mode) {
    return (
      <div
        className="hv-card p-3 border-[#A78BFA]/30 bg-gradient-to-br from-[#A78BFA]/5 to-transparent"
        data-testid="deep-itinerary-disabled-banner"
      >
        <div className="flex items-center gap-2 text-xs text-[#C4B5FD]">
          <Brain className="w-4 h-4" />
          <span>
            <strong>Deep Mode is off.</strong> Activate it from the trip header pill to unlock
            per-stop deep research + an AI-composed portfolio itinerary.
          </span>
        </div>
      </div>
    );
  }

  const status = data?.status || "idle";
  const eta = data?.eta_minutes;
  const compiledAt = data?.compiled_at;

  return (
    <div className="space-y-3" data-testid="deep-itinerary-panel">
      <div className="hv-card p-3 border-[#A78BFA]/30 bg-gradient-to-br from-[#A78BFA]/5 to-transparent">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <Brain className="w-4 h-4 text-[#C4B5FD] shrink-0" />
            <div className="min-w-0">
              <div className="text-xs font-semibold text-[#C4B5FD]">Deep Itinerary Portfolio</div>
              <div className="text-[10px] text-[#a0aabf]">
                AI-composed long-form briefing book — full deep brief per picked stop, smart road notes, end-of-doc references.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {status === "compiling" && (
              <span
                className="inline-flex items-center gap-1.5 text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1"
                data-testid="deep-itinerary-status-compiling"
              >
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Compiling{eta ? ` · ~${eta} min` : ""} — you'll be notified
              </span>
            )}
            {status === "ready" && compiledAt && (
              <span
                className="inline-flex items-center gap-1.5 text-[11px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded px-2 py-1"
                data-testid="deep-itinerary-status-ready"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                Ready · {new Date(compiledAt).toLocaleString("en-GB")}
              </span>
            )}
            {status === "failed" && (
              <span
                className="inline-flex items-center gap-1.5 text-[11px] text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1"
                data-testid="deep-itinerary-status-failed"
              >
                <AlertTriangle className="w-3.5 h-3.5" />
                Failed
              </span>
            )}
            <button
              type="button"
              onClick={() => setOpenModal(true)}
              disabled={status === "compiling"}
              className="hv-btn hv-btn-primary text-xs"
              data-testid="deep-itinerary-compile-btn"
              title={status === "ready" ? "Re-compile with a different stop selection" : "Compile a deep portfolio itinerary"}
            >
              {status === "ready" ? <RefreshCw className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />}
              {status === "ready" ? "Recompile" : "Compile Deep Itinerary"}
            </button>
          </div>
        </div>
        {status === "failed" && data?.error && (
          <div className="mt-2 text-[11px] text-rose-200 border-l-2 border-rose-500 pl-2">
            {data.error}
          </div>
        )}
      </div>

      {data?.doc && (
        <div className="hv-card p-5 md:p-6" data-testid="deep-itinerary-doc">
          {data.doc.startsWith("# Trip itinerary — compose step failed") && (
            <div
              className="mb-4 px-3 py-2 rounded border border-amber-500/40 bg-amber-500/10 text-xs text-amber-100 flex items-center gap-2"
              data-testid="deep-itinerary-compose-failed-hint"
            >
              <AlertTriangle className="w-4 h-4 shrink-0" />
              <span>
                Hidro composed this from raw context — the AI service was busy. Hit <strong>Recompile</strong> in a minute to get the full narrative.
              </span>
            </div>
          )}
          <MarkdownView md={data.doc} />
        </div>
      )}

      {openModal && (
        <CompileDeepModal
          trip={trip}
          onClose={() => setOpenModal(false)}
          onLaunched={() => { refresh(); }}
        />
      )}
    </div>
  );
}

// ─── Post-trip debrief modal ──────────────────────────────────────────
export function DebriefModal({ trip, onClose, onCommitted }) {
  const { branding } = useBranding();
  const AI_NAME = branding?.ai_name || "Rogi";
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]); // [{role, text}]
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [stops, setStops] = useState([]);
  const [pendingNotes, setPendingNotes] = useState([]); // [{stop_id, summary, outcome, next_step}]
  const [err, setErr] = useState("");
  const [committing, setCommitting] = useState(false);
  const scrollRef = useRef(null);

  // Wave 19 #108 — multi-stop voice debrief. Rep dictates a single 5-min
  // monologue and Hidro slices it into one note per stop they mentioned.
  const stopList = useMemo(
    () => (stops || []).map((s, i) =>
      `${s.id} -> Stop ${i + 1}: ${s.title || s.address || "(unnamed)"}`,
    ).join("\n"),
    [stops],
  );
  const voice = useVoiceCapture({
    context: "trip_multi_stop_debrief",
    extraContext: stopList,
  });
  // When the multi-stop voice flow returns, fan its notes into
  // pendingNotes. Replace any prior entry for the same stop_id.
  useEffect(() => {
    if (!voice.result) return;
    const incoming = voice.result?.extracted?.notes || [];
    if (!Array.isArray(incoming) || incoming.length === 0) return;
    setPendingNotes((prev) => {
      const next = [...prev];
      for (const n of incoming) {
        const idx = next.findIndex((x) => x.stop_id === n.stop_id);
        if (idx >= 0) next[idx] = { ...next[idx], ...n };
        else next.push(n);
      }
      return next;
    });
    // Surface a system message in the chat thread so the rep can see
    // what just happened — they can correct any mismatches.
    setMessages((m) => [
      ...m,
      {
        role: "ai",
        text: `Captured ${incoming.length} stop note${incoming.length === 1 ? "" : "s"} from your dictation. Review them on the right and edit any name or detail Whisper got wrong before saving.`,
      },
    ]);
    voice.setResult(null);
    // eslint-disable-next-line
  }, [voice.result]);

  // Start the session on open
  useEffect(() => {
    if (!trip?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(`/trips/${trip.id}/debrief/start`);
        if (cancelled) return;
        setSessionId(data.session_id);
        setStops(data.stops || []);
        setMessages([{
          role: "ai",
          text: `Hi — let's debrief ${data.title || "the trip"}. Tell me about the first stop — who did you meet, what did they say, and what's the next step? I'll capture proper meeting notes as we go.`,
        }]);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Could not start debrief");
      }
    })();
    return () => { cancelled = true; };
  }, [trip?.id]);

  // Auto-scroll to latest message
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Detect proposed meeting notes in the AI reply (fenced block format
  // emitted by the system prompt) and pull them into pendingNotes for
  // review/commit.
  const extractProposedNotes = (text) => {
    const re = /```meeting_note\s+([\s\S]*?)```/g;
    const found = [];
    let m;
    while ((m = re.exec(text)) !== null) {
      const block = m[1];
      const obj = {};
      block.split("\n").forEach((line) => {
        const km = line.match(/^([a-zA-Z_]+):\s*(.*)$/);
        if (km) obj[km[1].trim()] = km[2].trim();
      });
      if (obj.summary) found.push(obj);
    }
    return found;
  };

  const send = async () => {
    const text = input.trim();
    if (!text || !sessionId || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "me", text }]);
    setBusy(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/debrief/turn`, {
        session_id: sessionId,
        message: text,
      });
      const reply = data?.reply || "_(no reply)_";
      setMessages((m) => [...m, { role: "ai", text: reply }]);
      const proposed = extractProposedNotes(reply);
      if (proposed.length > 0) {
        setPendingNotes((p) => {
          // Merge: replace by stop_id if exists, else append
          const next = [...p];
          for (const note of proposed) {
            const idx = next.findIndex((n) => n.stop_id === note.stop_id);
            if (idx >= 0) next[idx] = { ...next[idx], ...note };
            else next.push(note);
          }
          return next;
        });
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || `${AI_NAME} failed to reply.`);
    }
    setBusy(false);
  };

  const commit = async () => {
    if (pendingNotes.length === 0) return;
    setCommitting(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/debrief/commit`, {
        session_id: sessionId,
        notes: pendingNotes,
      });
      onCommitted?.(data);
      onClose?.();
      // Wave 19 #108 — after a trip debrief, prompt the rep for a quick
      // personal check-in (their day overall, not the trip specifically).
      // Layout's CoachCheckinModal listens for this event.
      setTimeout(() => {
        try { window.dispatchEvent(new Event("hv:open-coach")); } catch (_e) { /* noop */ }
      }, 400);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not commit notes.");
    }
    setCommitting(false);
  };

  const editNote = (idx, patch) => {
    setPendingNotes((p) => p.map((n, i) => (i === idx ? { ...n, ...patch } : n)));
  };
  const removeNote = (idx) => {
    setPendingNotes((p) => p.filter((_, i) => i !== idx));
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Day's debrief — ${trip?.title || "Trip"}`}
      testId="debrief-modal"
      maxWidth="max-w-7xl"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 min-h-[55vh]">
        {/* LEFT — chat thread */}
        <div className="flex flex-col border border-white/10 rounded-md overflow-hidden bg-black/20">
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-[#1fb6c1] border-b border-white/10 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> {AI_NAME} debrief
            <span className="ml-auto text-[10px] normal-case tracking-normal text-[#647087]" title={`Tap the mic in the message box and talk for 5 min covering all your stops — ${AI_NAME} auto-slices into per-stop notes`}>
              tap mic to dictate full day
            </span>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-auto p-3 space-y-2 text-sm" data-testid="debrief-thread">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "me" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-2 text-xs whitespace-pre-wrap ${
                  m.role === "me"
                    ? "bg-[#1fb6c1]/15 border border-[#1fb6c1]/30 text-[#dde3ec]"
                    : "bg-white/5 border border-white/10 text-[#dde3ec]"
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="rounded-lg px-3 py-2 text-xs bg-white/5 border border-white/10 text-[#a0aabf] flex items-center gap-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> Hidro is thinking…
                </div>
              </div>
            )}
          </div>
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="border-t border-white/10 p-2 flex items-center gap-2"
          >
            <div className="relative flex-1">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Tell Hidro what happened — or tap the mic to dictate the whole day…"
                className="hv-input w-full text-xs pr-9"
                data-testid="debrief-input"
                disabled={!sessionId || busy || voice.recording || voice.uploading}
              />
              {/* Inline mic — same pattern as VoiceTextarea / MeetingDetailModal */}
              <div className="absolute top-1/2 -translate-y-1/2 right-1.5 flex items-center gap-1">
                {voice.recording && (
                  <span
                    className="text-[10px] font-mono text-rose-300 px-1.5 py-0.5 rounded bg-rose-500/10 border border-rose-500/30"
                    data-testid="debrief-voice-elapsed"
                  >
                    {Math.floor(voice.elapsedSec / 60)}:{String(voice.elapsedSec % 60).padStart(2, "0")}
                  </span>
                )}
                {voice.uploading ? (
                  <button
                    type="button"
                    disabled
                    className="w-7 h-7 rounded-full flex items-center justify-center bg-[#1FB6C1]/15 border border-[#1FB6C1]/40 text-[#1FB6C1]"
                    title="Slicing your day…"
                    data-testid="debrief-voice-uploading"
                  >
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  </button>
                ) : voice.recording ? (
                  <button
                    type="button"
                    onClick={voice.stop}
                    className="w-7 h-7 rounded-full flex items-center justify-center bg-rose-500/20 border border-rose-500/50 text-rose-300 hover:bg-rose-500/30 transition-colors"
                    title="Stop recording"
                    data-testid="debrief-voice-stop"
                    style={{
                      boxShadow: voice.level > 0
                        ? `0 0 ${4 + voice.level * 14}px rgba(244,63,94,${0.3 + voice.level * 0.4})`
                        : "none",
                    }}
                  >
                    <MicOff className="w-3.5 h-3.5" />
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={voice.start}
                    disabled={!sessionId || busy}
                    className="w-7 h-7 rounded-full flex items-center justify-center bg-white/5 border border-white/15 text-[#a0aabf] hover:bg-[#A78BFA]/15 hover:border-[#A78BFA]/40 hover:text-[#C4B5FD] transition-colors"
                    title="Dictate the full day — Hidro auto-slices into per-stop notes"
                    data-testid="debrief-voice-start"
                  >
                    <Mic className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
            <button
              type="submit"
              disabled={!sessionId || busy || !input.trim()}
              className="hv-btn hv-btn-primary text-xs"
              data-testid="debrief-send"
            >
              <Send className="w-3 h-3" />
            </button>
          </form>
        </div>

        {/* RIGHT — proposed notes the user can confirm + commit */}
        <div className="flex flex-col border border-white/10 rounded-md overflow-hidden bg-black/20">
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider text-[#A78BFA] border-b border-white/10 flex items-center gap-1.5">
            <CheckCircle2 className="w-3 h-3" /> Proposed meeting notes ({pendingNotes.length})
          </div>
          <div className="flex-1 overflow-auto p-3 space-y-2" data-testid="debrief-pending-notes">
            {pendingNotes.length === 0 && (
              <div className="text-[11px] text-[#647087] text-center py-6">
                As you describe each meeting, Hidro will draft notes here. You can edit them before saving.
              </div>
            )}
            {pendingNotes.map((n, i) => {
              const stop = stops.find((s) => s.id === n.stop_id);
              return (
                <div key={i} className="border border-white/10 rounded p-2 space-y-1.5 bg-white/[0.02]">
                  <div className="flex items-center justify-between gap-2 text-[10px] text-[#a0aabf]">
                    <span className="font-mono">{stop?.title || n.stop_id || "Stop"}</span>
                    <button
                      type="button"
                      onClick={() => removeNote(i)}
                      className="text-rose-300 hover:text-rose-200"
                      title="Discard this note"
                    >
                      <XIcon className="w-3 h-3" />
                    </button>
                  </div>
                  <DictTextarea
                    rows={2}
                    value={n.summary || ""}
                    onChange={(e) => editNote(i, { summary: e.target.value })}
                    className="hv-input w-full text-xs"
                    placeholder="Summary"
                    data-testid={`debrief-note-summary-${i}`}
                  />
                  <div className="grid grid-cols-2 gap-1.5">
                    <select
                      value={n.outcome || ""}
                      onChange={(e) => editNote(i, { outcome: e.target.value })}
                      className="hv-input text-xs"
                      data-testid={`debrief-note-outcome-${i}`}
                    >
                      <option value="">— outcome —</option>
                      <option value="warm">warm</option>
                      <option value="hot">hot</option>
                      <option value="cold">cold</option>
                      <option value="won">won</option>
                      <option value="lost">lost</option>
                      <option value="follow_up">follow up</option>
                    </select>
                    <input
                      type="text"
                      value={n.next_step || ""}
                      onChange={(e) => editNote(i, { next_step: e.target.value })}
                      placeholder="Next step"
                      className="hv-input text-xs"
                      data-testid={`debrief-note-next-${i}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
          <div className="border-t border-white/10 p-2 flex items-center justify-between gap-2">
            <span className="text-[10px] text-[#647087]">
              Saving will append to each lead's comments.
            </span>
            <button
              type="button"
              onClick={commit}
              disabled={committing || pendingNotes.length === 0}
              className="hv-btn hv-btn-primary text-xs"
              data-testid="debrief-commit"
            >
              <CheckCircle2 className="w-3 h-3" />
              {committing ? "Saving…" : `Save ${pendingNotes.length} note${pendingNotes.length === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      </div>
      {(err || voice.error) && (
        <div className="mt-2 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded px-2 py-1.5">
          {err || voice.error}
        </div>
      )}
    </Modal>
  );
}
